import {
  ArrowBack,
  CheckCircleOutline,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { Button } from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { AuthPageLayout } from "@/components/auth";
import { AuthFormError, AuthInput, AuthSubmitButton } from "@/components/auth/AuthFormControls";
import AuthTransition from "@/components/auth/AuthTransition";
import SignInPanel from "@/components/auth/SignInPanel";
import { UserService } from "@/services/features/UserService";
import { getPasswordStrength } from "@/utils/password";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isCodeStep = location.pathname === "/reset-password";
  const userService = UserService.getInstance();
  const initialEmail = searchParams.get("email") ?? "";
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorState, setErrorState] = useState({ message: "", visible: false });
  const setError = (message: string) => {
    setErrorState((previous) => ({
      message: message || previous.message,
      visible: Boolean(message),
    }));
  };
  const [notice, setNotice] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(isCodeStep && initialEmail ? 60 : 0);
  const busy = isLoading || isResending;
  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timeout = setTimeout(() => setResendSeconds((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timeout);
  }, [resendSeconds]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError("");
    setNotice("");

    if (!isCodeStep) {
      setIsLoading(true);
      try {
        await userService.requestPasswordResetCode(email.trim());
        setResendSeconds(60);
        void navigate(`/reset-password?email=${encodeURIComponent(email.trim())}`);
      } catch (err) {
        setError(
          axios.isAxiosError(err) && err.response?.data?.metadata?.message
            ? err.response.data.metadata.message
            : t("auth.errors.unexpectedErrorOccurredTryAgain"),
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!passwordStrength.isValid) {
      setError(t("auth.resetPassword.errors.passwordRequirements"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("auth.resetPassword.errors.passwordsDoNotMatch"));
      return;
    }

    setIsLoading(true);
    try {
      await userService.resetPasswordWithCode(email.trim(), code, password);
      setIsComplete(true);
    } catch (err) {
      setError(
        axios.isAxiosError(err) && err.response?.status === 401
          ? t("auth.resetPassword.errors.invalidOrExpiredCode")
          : t("auth.errors.unexpectedErrorOccurredTryAgain"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (busy || !email.trim() || resendSeconds > 0) return;
    setError("");
    setNotice("");
    setIsResending(true);
    try {
      await userService.requestPasswordResetCode(email.trim());
      setResendSeconds(60);
      setNotice(t("auth.resetPassword.codeResent"));
    } catch {
      setError(t("auth.errors.unexpectedErrorOccurredTryAgain"));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthPageLayout>
      <SignInPanel
        title={t(
          isComplete
            ? "auth.resetPassword.successTitle"
            : isCodeStep
              ? "auth.resetPassword.title"
              : "auth.forgotPassword.title",
        )}
        description={t(
          isComplete
            ? "auth.resetPassword.successDescription"
            : isCodeStep
              ? "auth.resetPassword.description"
              : "auth.forgotPassword.description",
        )}
      >
        <AuthTransition in={isComplete}>
          <div className="text-center">
            <CheckCircleOutline sx={{ fontSize: 64, color: "success.main" }} />
            <AuthSubmitButton type="button" onClick={() => void navigate("/login")}>
              {t("auth.resetPassword.actions.backToLogin")}
            </AuthSubmitButton>
          </div>
        </AuthTransition>
        <AuthTransition in={!isComplete}>
          <form onSubmit={(event) => void handleSubmit(event)}>
            <div>
              <label htmlFor="email" className="block text-md mb-2 text-left rtl:text-right">
                {t("auth.forgotPassword.fields.email")}
              </label>
              <AuthInput
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t("auth.forgotPassword.placeholders.email")}
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                disabled={busy}
              />
            </div>
            <AuthTransition in={isCodeStep}>
              <div className="space-y-4 pt-4" inert={!isCodeStep}>
                <div>
                  <label htmlFor="code" className="block text-md mb-2 text-left rtl:text-right">
                    {t("auth.resetPassword.fields.code")}
                  </label>
                  <AuthInput
                    id="code"
                    placeholder={t("auth.resetPassword.placeholders.code")}
                    name="code"
                    type="text"
                    required
                    disabled={busy}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="text-center rtl:text-center tracking-[0.5em] placeholder:tracking-normal"
                  />
                </div>

                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-md mb-2 text-left rtl:text-right"
                  >
                    {t("auth.resetPassword.fields.newPassword")}
                  </label>
                  <div className="relative">
                    <AuthInput
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      required
                      disabled={busy}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="new-password"
                      className="pr-12 rtl:pr-4 rtl:pl-12"
                    />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setShowPassword((visible) => !visible)}
                      aria-label={t("login.accessibility.togglePasswordVisibility")}
                      className="absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 px-4 flex items-center cursor-pointer disabled:cursor-not-allowed"
                    >
                      {showPassword ? (
                        <VisibilityOffOutlined sx={{ fontSize: 20, color: "#9ca3af" }} />
                      ) : (
                        <VisibilityOutlined sx={{ fontSize: 20, color: "#9ca3af" }} />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-md mb-2 text-left rtl:text-right"
                  >
                    {t("auth.resetPassword.fields.confirmPassword")}
                  </label>
                  <AuthInput
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    required
                    disabled={busy}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <p className="text-xs text-gray-500">
                  {t("auth.resetPassword.passwordRequirements")}
                </p>
              </div>
            </AuthTransition>
            <AuthFormError message={errorState.message} visible={errorState.visible} />
            <div role="status" aria-hidden={!notice}>
              <AuthTransition in={Boolean(notice)}>
                <p className="pt-4 text-sm text-green-700 text-center">{notice}</p>
              </AuthTransition>
            </div>
            <AuthSubmitButton disabled={busy}>
              {t(
                isCodeStep
                  ? isLoading
                    ? "auth.resetPassword.actions.resetting"
                    : "auth.resetPassword.actions.submit"
                  : isLoading
                    ? "auth.forgotPassword.actions.sending"
                    : "auth.forgotPassword.actions.submit",
              )}
            </AuthSubmitButton>
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                type="button"
                disabled={busy}
                startIcon={<ArrowBack className="rtl:rotate-180" />}
                onClick={() => void navigate("/login")}
                sx={{ flex: 1, minWidth: 0 }}
              >
                {t("auth.forgotPassword.actions.backToLogin")}
              </Button>
              <Button
                type="button"
                disabled={busy || (isCodeStep && (resendSeconds > 0 || !email.trim()))}
                onClick={() => {
                  if (isCodeStep) void handleResend();
                  else {
                    setError("");
                    void navigate("/reset-password");
                  }
                }}
                sx={{ flex: 1, minWidth: 0 }}
              >
                {isCodeStep
                  ? resendSeconds > 0
                    ? t("auth.resetPassword.actions.resendIn", { seconds: resendSeconds })
                    : t(
                        isResending
                          ? "auth.resetPassword.actions.resending"
                          : "auth.resetPassword.actions.resend",
                      )
                  : t("auth.forgotPassword.actions.alreadyHaveCode")}
              </Button>
            </div>
          </form>
        </AuthTransition>
      </SignInPanel>
    </AuthPageLayout>
  );
};

export default ResetPassword;
